import {
  setMinutes,
  setSeconds,
  setMilliseconds,
  format,
  isToday,
  parseISO,
  formatDistanceToNow,
  parse,
  isValid,
  startOfWeek,
  addDays,
  eachDayOfInterval,
} from 'date-fns';
import { getDateFnsLocale } from '@/lib/i18n/dateLocales';
import i18n from '@/lib/i18n/i18n';

function roundToNearestFiveMinutes(date: Date): Date {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 5) * 5; // Rounds up to nearest 5
  const newDate = setMinutes(date, roundedMinutes); // Sets new rounded minutes
  return setSeconds(setMilliseconds(newDate, 0), 0); // Clear seconds and milliseconds
}

function getFormattedTimeAndRelative(dateString: string = '') {
  if (dateString === '' || !dateString) return { timeIn12h: '', relativeTimeDesc: '' };

  const locale = getDateFnsLocale();
  const date = new Date(dateString);
  const timeIn12h = format(date, 'h:mm a', { locale });
  let relativeTimeDesc: string;
  if (isToday(date)) {
    relativeTimeDesc = i18n.t('common:DATE.TODAY', 'Today');
  } else {
    relativeTimeDesc = format(date, 'E, MMM d, yyyy', { locale });
  }

  return { timeIn12h, relativeTimeDesc };
}

function getDayFromDate(dateString: string) {
  if (!dateString) return '';
  const locale = getDateFnsLocale();
  const date = parseISO(dateString);
  // Day of month (number) is not really localized, but this is fine
  return format(date, 'd', { locale });
}

function getDayofTheWeekFromDate(dateString: string) {
  if (!dateString) return '';
  const locale = getDateFnsLocale();
  const date = parseISO(dateString);
  // Localized weekday name: "Monday" / "lundi" / "Montag"
  return format(date, 'EEEE', { locale });
}

function getMonthFromDate(dateString: string) {
  if (!dateString) return '';
  const locale = getDateFnsLocale();
  const date = parseISO(dateString);
  // Localized month name: "January" / "janvier" / "Januar"
  return format(date, 'LLLL', { locale });
}

function getTimeFromDate(dateString: string): string {
  if (!dateString) return '';
  const locale = getDateFnsLocale();
  const date = parseISO(dateString);
  // Localized time (12/24h depends on locale)
  return format(date, 'p', { locale });
}

function formatRelativeTimeFromTimeStamp(
  timestamp: number | string,
): string {
  if (!timestamp) return '';
  const locale = getDateFnsLocale();

  // coerce numeric string → number
  const ms = typeof timestamp === 'string'
    ? parseInt(timestamp, 10)
    : timestamp;

  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) {
    throw new Error(`Invalid timestamp: ${timestamp}`);
  }

  // `formatDistanceToNow` has its own localized words via locale
  return formatDistanceToNow(date, { addSuffix: true, locale });
}

function formatToFullMonthDayYear(dateString: string): string {
  if (!dateString) return '';
  const locale = getDateFnsLocale();
  const date = parseISO(dateString);
  // `PPP` → localized long date (e.g. "January 1, 2025" / "1 janvier 2025")
  return format(date, 'PPP', { locale });
}
function formatToMonthDayYear(dateString: string): string {
  if (!dateString) return '';
  const locale = getDateFnsLocale();
  const date = parseISO(dateString);
  // `PP` → localized medium date (e.g. "Jan 1, 2025" / "1 janv. 2025")
  return format(date, 'PP', { locale });
}

function toDateDisplay(date?: string): string {
  if (!date) return '-';
  const formatted = formatToMonthDayYear(date);
  return formatted || '-';
}

function getInitialScheduledAtSessionDate(
  mode: 'create' | 'update' | 'readonly',
  dateParam: string | null,
  initialScheduledAt?: Date,
): Date {
  if (mode === 'create' && dateParam) {
    const parsedDate = parse(dateParam, 'yyyy-MM-dd', new Date());
    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }
  return initialScheduledAt || new Date();
}

function getLocalizedMonths() {
  const locale = getDateFnsLocale();
  return Array.from({ length: 12 }, (_, i) => format(new Date(0, i), 'MMMM', { locale }));
}

function getLocalizedDaysOfWeek(): string[] {
  const locale = getDateFnsLocale();
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });

  // Generate 7 days starting from Monday with localized abbreviated names
  return Array.from({ length: 7 }, (_, i) => {
    const day = addDays(monday, i);
    // 'EEE' gives abbreviated weekday (Mon, Tue, Wed, etc.)
    return format(day, 'EEE', { locale }).toUpperCase();
  });
}

function getTimezoneLabel(timezone: string): string {
  if (timezone === 'UTC') return 'UTC';
  const [region, ...locationParts] = timezone.split('/');
  if (locationParts.length === 0) return timezone.replaceAll('_', ' ');
  return `${locationParts.join(' / ').replaceAll('_', ' ')} (${region.replaceAll('_', ' ')})`;
}

function getTimezoneOptions(selectedTimezone: string): string[] {
  let supportedTimezones: string[] = [];
  try {
    supportedTimezones = typeof Intl.supportedValuesOf === 'function'
      ? Intl.supportedValuesOf('timeZone')
      : [];
  } catch {
    supportedTimezones = [];
  }

  return Array.from(new Set(['UTC', selectedTimezone, ...supportedTimezones]))
    .filter(Boolean)
    .sort((first, second) => getTimezoneLabel(first).localeCompare(getTimezoneLabel(second)));
}

// generate grid data (flat array of weeks)
export function getCalendarWeeks(startDate: Date, endDate: Date) {
  const daysInView = eachDayOfInterval({ start: startDate, end: endDate });
  const weeks = [];
  for (let i = 0; i < daysInView.length; i += 7) {
    weeks.push(daysInView.slice(i, i + 7));
  }
  return weeks;
}

export {
  roundToNearestFiveMinutes,
  getFormattedTimeAndRelative,
  formatRelativeTimeFromTimeStamp,
  getDayFromDate,
  getDayofTheWeekFromDate,
  getMonthFromDate,
  getTimeFromDate,
  formatToFullMonthDayYear,
  formatToMonthDayYear,
  toDateDisplay,
  getInitialScheduledAtSessionDate,
  getLocalizedMonths,
  getLocalizedDaysOfWeek,
  getTimezoneLabel,
  getTimezoneOptions,
};
